type RectLike = {
  bottom: number;
  height: number;
  left: number;
  right: number;
  top: number;
  width: number;
};

type CalculateChartPlotBoundsParams = {
  marginTop: number;
  svgRect: RectLike;
  xAxisRect: RectLike | null;
  xAxisTickRects?: RectLike[];
  yAxisRect?: RectLike | null;
  yAxisTickLabelRects?: RectLike[];
};

type ChartPlotBounds = {
  height: number;
  left: number;
  top: number;
  width: number;
};

const PLOT_BOUNDS_LABEL_GUTTER = 6;

export const calculateChartPlotBounds = ({
  marginTop,
  svgRect,
  xAxisRect,
  xAxisTickRects = [],
  yAxisRect,
  yAxisTickLabelRects = [],
}: CalculateChartPlotBoundsParams): ChartPlotBounds | null => {
  if (xAxisRect === null || svgRect.width <= 0 || svgRect.height <= 0) {
    return null;
  }

  const tickLabelRightEdges = yAxisTickLabelRects
    .map((rect) => rect.right - svgRect.left)
    .filter((value) => Number.isFinite(value));
  const maxTickLabelRight =
    tickLabelRightEdges.length > 0 ? Math.max(...tickLabelRightEdges) : null;
  const xAxisTickCentres = xAxisTickRects
    .map((rect) => (rect.left + rect.right) / 2 - svgRect.left)
    .filter((value) => Number.isFinite(value));
  const minXAxisTickCentre = xAxisTickCentres.length > 0 ? Math.min(...xAxisTickCentres) : null;
  const maxXAxisTickCentre = xAxisTickCentres.length > 0 ? Math.max(...xAxisTickCentres) : null;
  const axisLeft = yAxisRect ? yAxisRect.left - svgRect.left : xAxisRect.left - svgRect.left;
  const baseLeft =
    maxTickLabelRight === null
      ? axisLeft
      : Math.max(axisLeft, maxTickLabelRight + PLOT_BOUNDS_LABEL_GUTTER);
  const rawLeft = minXAxisTickCentre ?? baseLeft;
  const rawTop = yAxisRect ? yAxisRect.top - svgRect.top : marginTop;
  const rawRight = maxXAxisTickCentre ?? xAxisRect.right - svgRect.left;
  const rawBottom = xAxisRect.top - svgRect.top;
  const left = Math.max(0, Math.min(rawLeft, svgRect.width));
  const top = Math.max(0, Math.min(Math.ceil(rawTop), svgRect.height));
  const right = Math.max(left, Math.min(rawRight, svgRect.width));
  const bottom = Math.max(top, Math.min(Math.floor(rawBottom), svgRect.height));
  const width = Math.max(0, right - left);
  const height = Math.max(0, bottom - top);

  if (width <= 0 || height <= 0) {
    return null;
  }

  return { left, top, width, height };
};
